import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import { ColumnIndex } from "@tento-core/column-index";
import { Rational } from "@tento-core/rational";
import { NaiveDate, PartialDate, TimezoneRegion } from "@tento-chrono";
import { FlightInfo } from "./flight-info.ts";
import { FlightMapper } from "./flight-mapper.ts";
import { ResolvedIndex } from "./resolved-index.ts";

/**
 * A column-index mapper that follows the traveller across flights, resolving
 * each column in the timezone of that day.
 */
export class IndexMapperTimeTraveler<Info>
  implements ColumnIndex.Mapper<FlightInfo<Info>>
{
  #resolveDefaultCache = new Map<string, ColumnIndex.Key.Resolved.Default>();

  constructor(
    readonly mapper: FlightMapper<Info>,
    readonly anchor: NaiveDate.Partial,
    readonly defaultTz: TimezoneRegion,
  ) {}

  get startDate(): Option<NaiveDate> {
    return this.anchor.start;
  }

  #resolvedIndexToKey(
    ri: ResolvedIndex<Info>,
    offset: number,
  ): ColumnIndex.Key.Resolved<FlightInfo<Info>> {
    switch (ri.type) {
      case "departure":
        return new ColumnIndex.Key.Resolved.Default(
          PartialDate.fromDate(ri.date),
          ri.tz,
          offset,
          null,
          ri.tp.withUtcTz().toTz(ri.tz.tzAtMse(ri.tp.mse)).ndt,
        );
      case "arrival":
        return new ColumnIndex.Key.Resolved.Default(
          PartialDate.fromDate(ri.date),
          ri.tz,
          offset,
          ri.tp.withUtcTz().toTz(ri.tz.tzAtMse(ri.tp.mse)).ndt,
          null,
        );
      case "collapsed":
        // For collapsed segments, we have both arrival and departure times
        return new ColumnIndex.Key.Resolved.Default(
          PartialDate.fromDate(ri.date),
          ri.tz,
          offset,
          ri.arrivalTp.withUtcTz().toTz(ri.tz.tzAtMse(ri.arrivalTp.mse)).ndt,
          ri.departureTp.withUtcTz().toTz(ri.tz.tzAtMse(ri.departureTp.mse))
            .ndt,
        );
      case "ground":
        return new ColumnIndex.Key.Resolved.Default(
          PartialDate.fromDate(ri.date),
          ri.tz,
          offset,
          null,
          null,
        );
      case "in-flight": {
        return new ColumnIndex.Key.Resolved.Custom(ri.info, offset);
      }
    }
  }

  resolveKey(key: ColumnIndex.Key): ColumnIndex.Key.Resolved<FlightInfo<Info>> {
    switch (key.strategy) {
      case "offset": {
        const idx = nonnull(key.inner.index, "IndexMapper: offset key index");
        const ri = this.mapper.find(idx);
        return this.#resolvedIndexToKey(ri, idx);
      }
      case "pd": {
        const pd = nonnull(key.inner.pd, "IndexMapper: pd key date");
        const idx = this.mapper.toIndex(
          pd.start,
          nonnull(key.inner.resolution, "IndexMapper: pd key resolution"),
        );
        const ri = this.mapper.find(idx);
        return this.#resolvedIndexToKey(ri, idx);
      }
      case "pd-tz": {
        // Explicit timezone
        // todo: flight mapper needs a way to query for a given date + timezone
        //
        const pd = nonnull(key.inner.pd, "IndexMapper: pd-tz key date");
        const idx = this.mapper.toIndex(pd.start, "lower");
        const ri = this.mapper.find(idx);
        return this.#resolvedIndexToKey(ri, idx);
      }
    }
  }

  /**
   * Cheap check for "is this index a flight in-flight column?" used by
   * the cellWidth scroll path. Avoids constructing a full Resolved key.
   */
  isInFlightIndex(index: number): boolean {
    return this.mapper.find(index).type === "in-flight";
  }

  resolveKeyToDefault(
    key: ColumnIndex.Key,
    resolution: "lower" | "upper",
  ): ColumnIndex.Key.Resolved.Default {
    const cacheKey = ColumnIndex.resolveCacheKey(key, resolution);
    const cached = this.#resolveDefaultCache.get(cacheKey);
    if (cached) return cached;
    const result = this.#resolveKeyToDefaultInner(key, resolution, 0);
    this.#resolveDefaultCache.set(cacheKey, result);
    return result;
  }

  #resolveKeyToDefaultInner(
    key: ColumnIndex.Key,
    resolution: "lower" | "upper",
    recursionCnt: number,
  ): ColumnIndex.Key.Resolved.Default {
    if (recursionCnt > 20) {
      throw new Error("either 20 flights in a row or we have a bug");
    }

    const k = this.resolveKey(key);
    if (k instanceof ColumnIndex.Key.Resolved.Default) return k;

    return this.#resolveKeyToDefaultInner(
      ColumnIndex.Key.fromIndex(
        resolution === "lower" ? k.offset - 1 : k.offset + 1,
      ),
      resolution,
      recursionCnt + 1,
    );
  }

  indexOfKey(key: ColumnIndex.Key): { index: number; subindex: Rational } {
    const rkey = this.resolveKey(key);
    return { index: rkey.offset, subindex: new Rational(0, 1) };
  }
}
